// frontend-react/src/services/keeperService.js
import api from './apiService';

/**
 * Keeper Selection API Service
 * Handles all keeper-related API calls
 * Endpoints from Session 4
 */

export const keeperService = {
  /**
   * GET /api/leagues/{league_id}/teams/{team_id}/keepers/eligible
   * Get players eligible to be kept (have contracts)
   */
  getEligibleKeepers: async (leagueId, teamId) => {
    try {
      const response = await api.get(
        `/api/leagues/${leagueId}/teams/${teamId}/keepers/eligible`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching eligible keepers:', error);
      throw error;
    }
  },

  /**
   * GET /api/leagues/{league_id}/teams/{team_id}/keeper-selections
   * Get current keeper selections (all statuses: active, saved, final)
   */
  getKeeperSelections: async (leagueId, teamId) => {
    try {
      const response = await api.get(
        `/api/leagues/${leagueId}/teams/${teamId}/keeper-selections`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching keeper selections:', error);
      throw error;
    }
  },

  /**
   * GET /api/leagues/{league_id}/teams/{team_id}/keeper-selections/status
   * Get keeper status summary for team
   */
  getKeeperStatus: async (leagueId, teamId) => {
    try {
      const response = await api.get(
        `/api/leagues/${leagueId}/teams/${teamId}/keeper-selections/status`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching keeper status:', error);
      throw error;
    }
  },

  /**
   * POST /api/leagues/{league_id}/teams/{team_id}/keepers/save
   * Save keeper draft with position assignments
   * Request: { keeper_assignments: [{player_id: 660271, roster_position: 'SS_0'}] }
   */
  saveKeeperDraft: async (leagueId, teamId, keeperAssignments) => {
    try {
      const response = await api.post(
        `/api/leagues/${leagueId}/teams/${teamId}/keepers/save`,
        { keeper_assignments: keeperAssignments }
      );
      return response.data;
    } catch (error) {
      console.error('Error saving keeper draft:', error);
      throw error;
    }
  },

  /**
   * PUT /api/leagues/{league_id}/teams/{team_id}/keepers/extension
   * Update a keeper's extension selection (handles cash delta for submitted keepers)
   * Request: { player_id: 660271, extension_type: 'OP1', extension_salary: 35.0 }
   */
  updateKeeperExtension: async (leagueId, teamId, data) => {
    try {
      const response = await api.put(
        `/api/leagues/${leagueId}/teams/${teamId}/keepers/extension`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error updating keeper extension:', error);
      throw error;
    }
  },

  /**
   * POST /api/leagues/{league_id}/teams/{team_id}/keepers/submit
   * Submit keeper selections (saved → final)
   */
  submitKeepers: async (leagueId, teamId) => {
    try {
      const response = await api.post(
        `/api/leagues/${leagueId}/teams/${teamId}/keepers/submit`
      );
      return response.data;
    } catch (error) {
      console.error('Error submitting keepers:', error);
      throw error;
    }
  },

  /**
   * POST /api/leagues/{league_id}/teams/{team_id}/keepers/unsubmit
   * Unsubmit keeper selections (final → saved)
   * Only allowed during SETUP mode
   */
  unsubmitKeepers: async (leagueId, teamId) => {
    try {
      const response = await api.post(
        `/api/leagues/${leagueId}/teams/${teamId}/keepers/unsubmit`
      );
      return response.data;
    } catch (error) {
      console.error('Error unsubmitting keepers:', error);
      throw error;
    }
  },

  /**
   * POST /api/leagues/{league_id}/teams/{team_id}/keepers/initialize
   * Initialize eligible keepers from contracts (Commissioner only)
   */
  initializeKeepers: async (leagueId, teamId) => {
    try {
      const response = await api.post(
        `/api/leagues/${leagueId}/teams/${teamId}/keepers/initialize`
      );
      return response.data;
    } catch (error) {
      console.error('Error initializing keepers:', error);
      throw error;
    }
  },

  /**
   * GET /api/leagues/{league_id}/keeper-selections/summary
   * Get league-wide keeper summary (Commissioner only)
   */
  getLeagueSummary: async (leagueId) => {
    try {
      const response = await api.get(
        `/api/leagues/${leagueId}/keeper-selections/summary`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching league keeper summary:', error);
      throw error;
    }
  }
};

export default keeperService;
